import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import {
  getMembershipVoucherStats,
  listMembershipVouchersForAdmin,
} from "../services/membership/MembershipVoucherService";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const formatWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const MembershipVoucherManagement = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redeemed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, summary] = await Promise.all([
        listMembershipVouchersForAdmin(),
        getMembershipVoucherStats(),
      ]);
      setRows(list);
      setStats(summary);
    } catch (e) {
      setError(e.message || "Failed to load vouchers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generatorsCount = useMemo(() => {
    const set = new Set(rows.map((r) => r.generatedByUid).filter(Boolean));
    return set.size;
  }, [rows]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: primaryBlue[800] }}>
          Membership Vouchers (₹699)
        </Typography>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4">{stats.total}</Typography>
            <Typography variant="caption">Generated</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4" color="success.main">
              {stats.active}
            </Typography>
            <Typography variant="caption">Active (unused)</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4" color="primary">
              {stats.redeemed}
            </Typography>
            <Typography variant="caption">Redeemed</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4">{generatorsCount}</Typography>
            <Typography variant="caption">Unique generators</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Points charged</TableCell>
                    <TableCell>Generated by</TableCell>
                    <TableCell>Generated at</TableCell>
                    <TableCell>Redeemed by</TableCell>
                    <TableCell>Redeemed at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No vouchers yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {v.code}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={v.status}
                            color={v.status === "active" ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {v.totalPointsDeducted != null
                            ? `${Number(v.totalPointsDeducted).toFixed(2)} pts`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{v.generatedByName || "—"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {v.generatedByEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatWhen(v.generatedAt)}</TableCell>
                        <TableCell>
                          {v.redeemedByEmail ? (
                            <>
                              <Typography variant="body2">
                                {v.redeemedByName || "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {v.redeemedByEmail}
                              </Typography>
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{formatWhen(v.redeemedAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MembershipVoucherManagement;
